import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";
import animalHash from "angry-purple-tiger";
import { capitalize, isEmpty, orderBy, partition } from "lodash";
import { stringifyUrl } from "query-string";
import { Fragment, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { HotspotWitness, PoCActivity } from ".";
import StatusDot from "../../../../components/StatusDot";
import TruncatedText from "../../../../components/TruncatedText";
import { useHotspots } from "../../../../context/hotspots";
import { useViewport } from "../../../../context/viewport";
import { avatarsApi, breakpoints } from "../../../../utils/constants";
import useMapConfig from "../../../../utils/useMapConfig";
import { renderDate } from "../HotspotInfoTab";

interface Props {
  activity: PoCActivity | null;
  open: boolean;
  onClose: () => void;
}

const ProofOfCoverageActivitySidebar: React.FC<Props> = ({
  activity,
  open,
  onClose
}) => {
  const { hotspotsMap } = useHotspots();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const renderDistance = useMapConfig((state) => state.renderDistance);

  const truncatedLength = useMemo(() => (isMobile ? 10 : 30), [isMobile]);

  const renderHotspot = useCallback(
    (hotspotId: string, location: string) => {
      const hotspot = hotspotsMap[hotspotId];
      return (
        <Link to={`/hotspots/${hotspotId}/status`} className="group">
          <div className="relative flex items-center space-x-3">
            <div className="flex-shrink-0 flex items-center">
              <span className="inline-block relative">
                <img
                  className="h-10 w-10 rounded-full"
                  src={stringifyUrl({
                    url: `${avatarsApi}/bottts/${hotspotId}.svg`,
                    query: { w: 40, h: 40 }
                  })}
                  alt="Hotspot avatar"
                />
                {hotspot && (
                  <StatusDot
                    hotspot={hotspot}
                    className="absolute top-0 right-0 block"
                    withoutTooltip
                  />
                )}
              </span>
            </div>
            <div className="flex-1 min-w-0 focus:outline-none">
              <span className="absolute inset-0" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate group-hover:underline">
                {animalHash(hotspotId)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {location}
              </p>
            </div>
          </div>
        </Link>
      );
    },
    [hotspotsMap]
  );

  const renderWitnesses = useCallback(
    (witnesses: HotspotWitness[]) =>
      orderBy(witnesses, "distance", "asc").map(
        ({ distance, hash, rssi, snr, location, invalid_reason }) => (
          <div
            key={hash}
            className="py-3 flex items-center justify-between text-sm font-normal"
          >
            <dt className="w-8/12">
              <div>{renderHotspot(hash, location)}</div>

              {invalid_reason && (
                <div className="text-sm font-normal text-red-500">
                  <span className="font-light text-gray-500 dark:text-gray-400">
                    Reason:{" "}
                  </span>
                  {capitalize(invalid_reason.replaceAll("_", " "))}
                </div>
              )}
            </dt>
            <dd className="flex flex-col justify-center items-end w-4/12 sm:text-sm text-xs text-gray-900 dark:text-white">
              <div>
                <span className="font-light text-gray-500 dark:text-gray-400">
                  Distance:{" "}
                </span>
                {renderDistance(distance, distance > 1000)}
              </div>
              <div>
                <span className="font-light text-gray-500 dark:text-gray-400">
                  RSSI:{" "}
                </span>
                {rssi.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
                dBm
              </div>
              <div>
                <span className="font-light text-gray-500 dark:text-gray-400">
                  SNR:{" "}
                </span>
                {snr.toLocaleString(undefined, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1
                })}
                dB
              </div>
            </dd>
          </div>
        )
      ),
    [renderDistance, renderHotspot]
  );

  const renderContent = useMemo(() => {
    if (!activity) return null;

    const [invalidWitnesses, validWitnesses] = partition(
      activity.witnesses,
      (witness) => !witness.valid
    );

    return (
      <div className="flex-1 py-6 divide-y divide-gray-200 dark:divide-gray-500 overflow-y-auto">
        <div className="px-4 sm:px-6 space-y-6">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                Witnesses
              </dt>

              <div className="flex items-baseline">
                <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                  {activity.witnesses.length}
                </dd>

                {activity.invalid_count > 0 && (
                  <div className="ml-2 flex items-baseline text-sm text-gray-500 dark:text-gray-400">
                    {activity.invalid_count} invalid
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Date</h3>
            <div className="mt-2">{renderDate(activity.time)}</div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">
              Transaction ID
            </h3>
            <TruncatedText
              className="mt-2"
              text={activity.hash}
              label="transaction ID"
              truncateLength={truncatedLength}
            />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Challenger</h3>
            <div className="mt-2">
              {renderHotspot(activity.challenger, activity.challenger_location)}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Beaconer</h3>
            <div className="mt-2">
              {renderHotspot(activity.beaconer, activity.beaconer_location)}
            </div>
          </div>

          {!isEmpty(invalidWitnesses) && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 flex items-center justify-between">
                <span>
                  Invalid witness{invalidWitnesses.length === 1 ? "" : "es"} (
                  {invalidWitnesses.length})
                </span>
                <span>Info</span>
              </h3>
              <dl className="mt-2 border-t border-gray-200 divide-y divide-gray-200">
                {renderWitnesses(invalidWitnesses)}
              </dl>
            </div>
          )}

          <div>
            <h3 className="text-sm font-medium text-gray-500 flex items-center justify-between">
              <span>
                Valid witness{validWitnesses.length === 1 ? "" : "es"} (
                {validWitnesses.length})
              </span>
              <span>Info</span>
            </h3>
            <dl className="mt-2 border-t border-gray-200 divide-y divide-gray-200">
              {isEmpty(validWitnesses)
                ? "No valid witnesses"
                : renderWitnesses(validWitnesses)}
            </dl>
          </div>
        </div>
      </div>
    );
  }, [activity, renderHotspot, renderWitnesses, truncatedLength]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={onClose}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 sm:max-w-full right-0 flex">
            <div className="absolute inset-0" onClick={onClose} />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-md dark:text-white">
                <div className="max-w-3xl xl:max-w-6xl h-full">
                  <div className="h-full flex flex-col bg-white shadow-xl dark:bg-gray-900">
                    <div className="flex-1 h-0 flex flex-col">
                      <div className="flex-none py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-medium text-white">
                            PoC activity
                          </Dialog.Title>
                          <div className="ml-3 h-7 flex items-center">
                            <button
                              type="button"
                              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                              onClick={onClose}
                            >
                              <span className="sr-only">Close panel</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {renderContent}
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ProofOfCoverageActivitySidebar;
